import type { EventBus } from "../core/EventBus.ts";
import { COLORS } from "../core/constants.ts";
import { t } from "../utils/i18n.ts";
import {
  MODAL_FRAME,
  MODAL_TEXT_FLOOR_ROLE,
  MODAL_TOUCH_FLOOR,
} from "./modalShell.ts";
import { UI_SPACE, UI_FONT_STACK, roleFontSize, withUiScale } from "./uiTokens.ts";

export interface AudioMixerInit {
  sfxMuted: boolean;
  musicMuted: boolean;
  sfxVolume: number;
  musicVolume: number;
}

export interface AudioMixer {
  el: HTMLElement;
  dispose(): void;
}

/**
 * Shared audio mixer (PT-19) — one widget. Music + Sound each get a mute toggle
 * and a volume slider. Its ONE host today is the in-run settings modal
 * (`SettingsOverlay`, via the HUD's single `createAudioMixer` call); before spec
 * `2026-pause-settings-overlays` Task-003 that host was the HUD's ⚙ dropdown.
 * The instance is REUSED across open/close, never rebuilt (spec AC-10).
 *
 * Single source of truth: toggles emit `audio.toggleMusic` / `audio.toggleMute`
 * and sliders emit `audio.setMusicVolume` / `audio.setSfxVolume` (REQUESTS);
 * the engine is the authority and echoes both mute AND (Task-004, 2026-audio-
 * module-audit, AC-6) volume state back — mute via `audio.musicMuteChanged` /
 * `audio.sfxMuteChanged`, volume via `audio.musicVolumeChanged` /
 * `audio.sfxVolumeChanged` — which this widget listens to so every instance
 * stays in sync (the in-bar SFX button, the HUD popover, and the start-screen
 * strip) AND so a boot-time direct engine call (main.ts applying a persisted
 * `MetaState` volume) reaches an already-mounted mixer's slider, not just a
 * live drag on this same widget (the HUD persisted-volume drift fix).
 */
export function createAudioMixer(
  bus: EventBus,
  init: AudioMixerInit,
): AudioMixer {
  injectStyles();

  const el = document.createElement("div");
  el.className = "audio-mixer";

  const music = buildRow("music", t("audio.music"), init.musicMuted, init.musicVolume);
  const sfx = buildRow("sfx", t("audio.sound"), init.sfxMuted, init.sfxVolume);
  el.appendChild(music.row);
  el.appendChild(sfx.row);

  // Toggles → engine events.
  music.toggle.addEventListener("click", () => bus.emit("audio.toggleMusic"));
  sfx.toggle.addEventListener("click", () => bus.emit("audio.toggleMute"));

  // Sliders → engine volume events.
  music.slider.addEventListener("input", () => {
    bus.emit("audio.setMusicVolume", clamp01(music.slider.valueAsNumber));
  });
  sfx.slider.addEventListener("input", () => {
    bus.emit("audio.setSfxVolume", clamp01(sfx.slider.valueAsNumber));
  });

  // Echo engine mute + volume state back into this mixer so the HUD popover
  // and the start-screen strip never drift apart — AND so a value applied
  // directly on the engine (main.ts boot-time persisted-settings apply, no
  // widget involved) still reaches an already-mounted slider. Task-004
  // (2026-audio-module-audit, AC-6): volume now listens to the engine's
  // authoritative ECHO (`audio.*VolumeChanged`), not its own outgoing
  // REQUEST (`audio.set*Volume`) — symmetric with how mute already listens
  // to `audio.*MuteChanged`, not `audio.toggle*`.
  const unsubs = [
    bus.on("audio.musicMuteChanged", (m: unknown) =>
      reflectMute(music, m as boolean),
    ),
    bus.on("audio.sfxMuteChanged", (m: unknown) =>
      reflectMute(sfx, m as boolean),
    ),
    bus.on("audio.musicVolumeChanged", (v: unknown) =>
      reflectVolume(music, v as number),
    ),
    bus.on("audio.sfxVolumeChanged", (v: unknown) =>
      reflectVolume(sfx, v as number),
    ),
  ];

  return {
    el,
    dispose() {
      for (const u of unsubs) u();
    },
  };
}

interface Row {
  bus: "music" | "sfx";
  row: HTMLElement;
  toggle: HTMLButtonElement;
  slider: HTMLInputElement;
  label: string;
}

function buildRow(
  busName: "music" | "sfx",
  label: string,
  muted: boolean,
  volume: number,
): Row {
  const row = document.createElement("div");
  row.className = "audio-mixer-row";
  row.dataset["bus"] = busName;

  const toggle = document.createElement("button");
  toggle.className = "audio-mixer-toggle";
  toggle.dataset["bus"] = busName;
  toggle.type = "button";

  const slider = document.createElement("input");
  slider.type = "range";
  slider.className = "audio-mixer-slider";
  slider.dataset["bus"] = busName;
  slider.min = "0";
  slider.max = "1";
  slider.step = "0.01";
  slider.value = String(clamp01(volume));
  slider.setAttribute("aria-label", `${label} ${t("audio.volume")}`);

  const r: Row = { bus: busName, row, toggle, slider, label };
  reflectMute(r, muted);

  row.appendChild(toggle);
  row.appendChild(slider);
  return r;
}

function reflectMute(r: Row, muted: boolean): void {
  const glyph = r.bus === "music" ? "♫" : "♪"; // ♫ / ♪
  r.toggle.textContent = `${glyph} ${r.label}`;
  r.toggle.classList.toggle("muted", muted);
  r.toggle.setAttribute("aria-pressed", String(muted));
}

/**
 * Mirror a volume change from another mixer instance (cross-instance sync)
 * without re-emitting — only updates the slider position when it actually
 * differs, so we never fight the user's in-flight drag on the same widget.
 */
function reflectVolume(r: Row, v: number): void {
  const next = clamp01(v);
  if (r.slider.valueAsNumber !== next) {
    r.slider.value = String(next);
  }
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

/**
 * The mixer's own stylesheet.
 *
 * ── Colour (spec `2026-pause-settings-overlays`, Task-003) ───────────────────
 * This widget carried four PRE-GLACIER green hex literals — the label ink
 * (twice), the muted-state ink and the slider's `accent-color` — plus two
 * decimal `rgba()` grass-green borders. Invisible in the old dark corner
 * dropdown; inside the mint `SENTINEL_GREEN` settings panel they read as a
 * second, older UI, which is exactly what the owner's "both overlays look like
 * parts of the same UI system" forbids. Every one of them now resolves from
 * `COLORS` through `color-mix()`, so this file carries ZERO inline hex — the
 * `tests/architecture/designTokens.test.ts` ratchet drops 4 → 0 (the entry
 * retires) in the same commit, which is that guard's own documented procedure.
 * The change is contained to this stylesheet: `createAudioMixer` has exactly
 * one call site (`HUD.ts`), so no other surface sees it.
 *
 * ── Size (the ≥11 CSS px FINAL floor) ────────────────────────────────────────
 * `♫ MUSIK` / `♪ TON` are the ONLY thing naming which slider is which, so they
 * are functional text and take `MODAL_TEXT_FLOOR_ROLE` (the shell's floor-
 * clearing role) instead of `micro` (12 design px → 8.69 CSS px final on an
 * iPhone SE). Every box-model length is `withUiScale`d for the same reason the
 * shell's are: this widget renders INSIDE a scaled modal, and flat px there
 * shrink relative to their own text on the phone tier.
 *
 * ── Touch ────────────────────────────────────────────────────────────────────
 * Both controls take the project's canonical `@media (pointer:coarse)` 44px
 * floor (`TowerSelector.ts:1057-1063`, `tests/ui/touchTargets.test.ts`): before
 * this, a native `input[type=range]` was ~20px tall and the toggle ~19px.
 * (`795-798` — the line range seven other files still cite for this — has not
 * pointed at the floor for some time; re-read 2026-08-10, it is the
 * `.tower-btn.selected` accent. Only this file's copy is corrected here; the
 * repo-wide stale citation is filed for a sweep of its own.)
 *
 * Task-005 (AC-11) made that floor `MODAL_TOUCH_FLOOR` — `max(44px, <44
 * counter-scaled>)` — because the bare literal was the SMALLER of the two on a
 * phone. Measured in a real Chromium on the booted page (2026-08-11, iPhone SE
 * landscape, `pointer:coarse` emulated): the slider was **6.67 CSS px** tall
 * un-floored, **18.33** at the flat 44 design px, and **31.87** at the
 * counter-scaled floor.
 *
 * ── The WIDTH axis has its own arm, and it is 104, not 44 ───────────────────
 * `max(44px, <44 counter-scaled>)` is a floor only for a control whose BASE is
 * 44. This toggle's base `min-width` is {@link TOGGLE_MIN_W} = 104 — the
 * mockup's own `.mix-lbl{min-width:calc(104*var(--k))}`
 * (`docs/pause_settings_overlays_2026-08.html:535`) — and `max(44, 44k) < 104k`
 * for EVERY k ≥ 1, so the 44-armed floor is a CAP on this axis, exactly like
 * the bare literal it replaced (`max(44, 44k)` = 76.51 design px on an SE, the
 * bare 44px = 44; the base is 180.85). Measured in the same real Chromium, DE,
 * iPhone SE landscape, `pointer:coarse` — and measured at `929bfbb` too, which
 * is the part that matters: **the rendered width is identical either way**
 * (`♫ MUSIK` 58.80 CSS px, `♪ TON` 46.37), because content, not the cap, holds
 * the line. So this is not a regression this task introduced; it is the SAME
 * defect on the axis the first pass did not look at, and it has shipped since
 * the flat floor was added. With the 104 arm restored both toggles render
 * **75.34 CSS px** — the drawn width — the German `♪ TON` gains 61 %, and the
 * two sliders start at the same x again (`text-align: left` above only means
 * something against a real minimum). The base and the coarse arm share ONE
 * constant so they cannot drift apart again, and
 * `tests/ui/touchTargets.test.ts` now resolves every coarse declaration in this
 * sheet against its own base rule, on BOTH axes, instead of only checking that
 * the floor is `max()`-shaped.
 *
 * ── Spacing (the ≥8 CSS px rail) ────────────────────────────────────────────
 * Two lengths here moved, and one of them is new: `.audio-mixer`'s row `gap`
 * (`sm` 8 → `lg` 12 — TWO steps) and its `margin-top` off the ALLES STUMM row
 * above, which did not exist. Measured pre-fix on the booted page (iPhone SE):
 * the mute row and the `♫ MUSIK` row **abutted at 0.000 CSS px**, and the two
 * mixer rows sat 5.79 apart; post-fix both are 8.69. `.audio-mixer-row`'s own
 * `gap` also moved `md` → `lg`, but it changes nothing rendered: in this
 * widget's ONE host the row also carries `.modal-shell-row`, whose `gap` is
 * `lg` in a later sheet at equal specificity and therefore wins (computed
 * `column-gap: 20.8669px` at `929bfbb` — the shell's value, not this file's).
 * It is a convergence onto the shell and onto the mockup's `.row`
 * (`gap:calc(12*var(--k))`, mockup:400), not a layout change.
 */
/**
 * The label box's minimum width, in design px — the mockup's
 * `.mix-lbl{min-width:calc(104*var(--k))}`
 * (`docs/pause_settings_overlays_2026-08.html:535`, read 2026-08-11). It is
 * what makes the two sliders start at the same x when the two labels are
 * different lengths, which in DE (`♫ MUSIK` 7 chars vs `♪ TON` 5) is every
 * time. A CONSTANT rather than two literals because it has to appear twice —
 * in the base rule and as the upper arm of the coarse floor — and the two
 * drifting apart is precisely the bug documented above.
 */
const TOGGLE_MIN_W = 104;

function injectStyles(): void {
  if (document.getElementById("audio-mixer-styles")) return;
  const G = COLORS.SENTINEL_GREEN;
  // BL-256 — the palette's AA-safe secondary-text token for the muted state
  // (SENTINEL_GREEN_DIM is fine for chrome but sub-AA as text).
  const GT = COLORS.TEXT_SECONDARY;
  const style = document.createElement("style");
  style.id = "audio-mixer-styles";
  style.textContent = `
    .audio-mixer {
      display: flex;
      flex-direction: column;
      gap: ${withUiScale(UI_SPACE.lg)};
      margin-top: ${withUiScale(UI_SPACE.lg)};
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize(MODAL_TEXT_FLOOR_ROLE)};
      color: ${COLORS.TEXT_PRIMARY};
      overflow-wrap: break-word;
      word-break: normal;
    }
    .audio-mixer-row {
      display: flex;
      align-items: center;
      gap: ${withUiScale(UI_SPACE.lg)};
    }
    .audio-mixer-toggle {
      flex: 0 0 auto;
      min-width: ${withUiScale(TOGGLE_MIN_W)};
      text-align: left;
      background: ${MODAL_FRAME.background};
      border: ${MODAL_FRAME.border};
      border-radius: ${withUiScale(UI_SPACE.xxs)};
      color: ${COLORS.TEXT_PRIMARY};
      font-family: ${UI_FONT_STACK};
      font-size: ${roleFontSize(MODAL_TEXT_FLOOR_ROLE)};
      letter-spacing: 1px;
      padding: ${withUiScale(UI_SPACE.xxs)} ${withUiScale(UI_SPACE.sm)};
      cursor: pointer;
    }
    .audio-mixer-toggle:hover,
    .audio-mixer-toggle:focus-visible {
      border-color: ${MODAL_FRAME.borderColorActive};
    }
    .audio-mixer-toggle.muted {
      color: ${GT};
      border-color: color-mix(in srgb, ${GT} 40%, transparent);
      text-decoration: line-through;
    }
    .audio-mixer-slider {
      flex: 1 1 auto;
      min-width: ${withUiScale(90)};
      accent-color: ${G};
      cursor: pointer;
    }
    /* BL-193 touch floor — see MODAL_TOUCH_FLOOR + TOGGLE_MIN_W. */
    @media (pointer:coarse) {
      .audio-mixer-toggle { min-height: ${MODAL_TOUCH_FLOOR}; min-width: max(44px, ${withUiScale(TOGGLE_MIN_W)}); }
      .audio-mixer-slider { min-height: ${MODAL_TOUCH_FLOOR}; }
    }
  `;
  document.head.appendChild(style);
}
